import { useCallback, useEffect, useRef } from 'react';

import { useSelector } from 'react-redux';

import { StreamTypes } from 'store/player/actions.types';
import { RBProgram } from 'store/railsBuilder/reducer.types';
import { selectCachedRecordingByEventId } from 'store/recordings/selectors';
import {
    selectAppSessionId,
    selectVideoSessionId,
} from 'store/session/selectors';

import { VideoItem, VodEditorial } from 'services/data-manager/types';
import {
    PlaybackStopUavParams,
    UavContentTypes,
    UavEvents,
} from 'services/uav/types';
import useGetBaseUavParams from 'services/uav/useGetBaseUavParams';
import {
    MOBILE_PLAYBACK_CONTENT_SOURCE,
    getPlayBackType,
} from 'services/uav/utils';

type GetPlaybackStopUavParamsProps = Omit<
    PlaybackStopUavParams['metadata'],
    | 'appSessionId'
    | 'sessionId'
    | 'contentType'
    | 'contentSource'
    | 'playbackSessionId'
    | 'appName'
    | 'appVersion'
    | 'deviceType'
>;

type Props = {
    playbackType: StreamTypes | undefined;
    videoItem: VideoItem | undefined;
};

const useGetPlaybackStopUavParams = ({ playbackType, videoItem }: Props) => {
    const appSessionId = useSelector(selectAppSessionId);
    const genericVideoSessionId = useSelector(selectVideoSessionId);
    const recordingItem = useSelector(
        selectCachedRecordingByEventId(videoItem?.id),
    );

    const videoSessionIdRef = useRef<string>(undefined);
    const previousPlayerData =
        useRef<{ videoItem: VideoItem; playbackType: StreamTypes }>(undefined);

    useEffect(() => {
        if (genericVideoSessionId) {
            videoSessionIdRef.current = genericVideoSessionId;
        }
    }, [genericVideoSessionId]);

    useEffect(() => {
        if (videoItem && playbackType) {
            previousPlayerData.current = { videoItem, playbackType };
        }
    }, [videoItem, playbackType]);

    const getBaseUavParams = useGetBaseUavParams();

    const getPlaybackStopUavParams = useCallback(
        (
            params: GetPlaybackStopUavParamsProps,
        ): PlaybackStopUavParams | undefined => {
            const baseUavParams = getBaseUavParams(UavEvents.PLAYBACK_STOP);

            if (baseUavParams && appSessionId && videoSessionIdRef.current) {
                const contentType = getPlayBackType(
                    previousPlayerData.current?.playbackType,
                    previousPlayerData.current?.videoItem?.originalProgram as
                        | VodEditorial
                        | RBProgram,
                );
                const recordingId =
                    (contentType === UavContentTypes.NPVR && {
                        recordingId: recordingItem?.id,
                    }) ||
                    undefined;

                return {
                    ...baseUavParams,
                    metadata: {
                        ...params,
                        appSessionId,
                        contentSource: MOBILE_PLAYBACK_CONTENT_SOURCE,
                        contentType,
                        playbackSessionId: videoSessionIdRef.current,
                        ...recordingId,
                        ...baseUavParams.metadata,
                    },
                };
            }

            return undefined;
        },
        [getBaseUavParams, appSessionId, recordingItem],
    );

    return {
        getPlaybackStopUavParams,
    };
};

export default useGetPlaybackStopUavParams;
